import { ensureLeadingSlash, isExternalUrl } from '@/lib/utils/link-utils'
import type { CloudflareConfig } from './CloudflareConfig.context'
import type { CloudflarePictureSizes } from './CloudflarePicture.client'

export const CF_IMAGE_PREFIX = '/cdn-cgi/image/'
export const CF_DEFAULT_QUALITY = 85

/**
 * The context in which the media is used, determining the appropriate Cloudflare domain.
 * 'pim' for default/public domain,
 * 'ecom' for internal domain
 * 'cms' for CMS domain.
 */
export type CloudflareMediaContext = 'pim' | 'ecom' | 'cms'

// Formats that will be generated for each size.
const cloudflareFormats = ['webp'] as const

function isLocalMediaUrl(src: string): boolean {
  return src.startsWith('/media')
}

type ResolveCloudflareDomainArgs = {
  config: CloudflareConfig
  context?: CloudflareMediaContext
  isInternalPicture?: boolean
}

export function resolveCloudflareDomain({
  config,
  context = 'pim',
  isInternalPicture = false,
}: ResolveCloudflareDomainArgs): string {
  if (context === 'cms') {
    return config.cmsDomain
  }

  if (context === 'ecom' || isInternalPicture) {
    return config.internalDomain
  }

  return config.cfDomain
}

type CloudflareDomainActionProps = {
  src: string
  width?: number[]
  height?: number[]
  fit: 'cover' | 'contain'
  quality?: number
  dpr?: number
  isInternalPicture?: boolean
  context?: CloudflareMediaContext
  config: CloudflareConfig
}

type BuildCloudflareUrlProps = {
  src: string
  width?: number
  height?: number
  fit: 'cover' | 'contain'
  quality?: number
  format?: string
  domain: string
  dpr?: number
}

export function buildCloudflareUrl({
  src,
  width,
  height,
  fit,
  quality = CF_DEFAULT_QUALITY,
  format = 'webp',
  domain,
  dpr,
}: BuildCloudflareUrlProps): string {
  const isLocalMedia = isLocalMediaUrl(src)
  // External URLs are passed as-is so Cloudflare can fetch from the correct origin (preserving port and query params).
  const originalPath = isExternalUrl(src) ? src : ensureLeadingSlash(src)
  const params = [
    width ? `width=${width}` : '',
    height ? `height=${height}` : '',
    `quality=${Math.max(1, Math.min(quality, 100))}`,
    `format=${format}`,
    `fit=${fit}`,
    dpr ? `dpr=${dpr}` : '',
  ]
    .filter(Boolean)
    .join(',')
  const separator = originalPath.startsWith('/') ? '' : '/'
  const imagePath = `${CF_IMAGE_PREFIX}${params}${separator}${originalPath}`

  if (isLocalMedia) return imagePath

  return `https://${domain}${imagePath}`
}

type CloudflareDomainActionReturn = Record<
  string,
  {
    url: string
    size: number | CloudflarePictureSizes
  }[]
>

export function getCloudflarePictureURLs({
  src,
  width = [],
  height = [],
  fit,
  quality = CF_DEFAULT_QUALITY,
  dpr,
  isInternalPicture = false,
  context = 'pim',
  config,
}: CloudflareDomainActionProps): CloudflareDomainActionReturn {
  const urls: CloudflareDomainActionReturn = {}

  // Determine the type of media
  // - If src starts with "/media", it's a local asset, so we use a relative URL (no domain prefix)
  // - Otherwise, resolve the domain from the shared Cloudflare context helper.
  const isLocalMedia = isLocalMediaUrl(src)
  const domain = isLocalMedia ? '' : resolveCloudflareDomain({ config, context, isInternalPicture })

  const sortedWidths = [...width].filter(Boolean).sort((a, b) => a - b)
  const sortedHeights = [...height].filter(Boolean).sort((a, b) => a - b)
  const hasWidth = sortedWidths.length > 0
  const referenceValues = hasWidth ? sortedWidths : [sortedHeights.at(-1) ?? 100]

  for (const format of cloudflareFormats) {
    const formatUrls: { url: string; size: number }[] = []

    for (const ref of referenceValues) {
      const height = sortedHeights.at(-1)
      formatUrls.push({
        url: buildCloudflareUrl({
          src,
          width: hasWidth ? ref : undefined,
          height,
          fit,
          quality,
          format,
          domain,
          dpr,
        }),
        size: ref,
      })
    }

    urls[format] = formatUrls
  }

  return urls
}
